import { useState, useEffect, useMemo } from 'react';
import { fetchPatientOutstandingClaims, createPaymentPlan } from '../services/api';
import './PaymentPlanModal.css';

const FREQUENCIES = [
  { value: 'weekly',    label: 'Weekly' },
  { value: 'bi_weekly', label: 'Bi-Weekly' },
  { value: 'monthly',   label: 'Monthly' },
];

function fmt(n) {
  return '$' + (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function buildSchedule(totalAmount, installmentAmount, frequency, startDate) {
  if (!totalAmount || !installmentAmount || !startDate) return [];
  const total   = parseFloat(totalAmount);
  const instAmt = parseFloat(installmentAmount);
  if (total <= 0 || instAmt <= 0) return [];

  const schedule = [];
  let remaining   = total;
  let current     = new Date(startDate + 'T12:00:00');
  let num         = 1;

  while (remaining > 0.01 && num <= 120) {
    const thisAmt = parseFloat(Math.min(instAmt, remaining).toFixed(2));
    schedule.push({ number: num, date: current.toISOString().slice(0, 10), amount: thisAmt });
    remaining -= thisAmt;
    num++;

    if (frequency === 'weekly')    { current.setDate(current.getDate() + 7); }
    else if (frequency === 'bi_weekly') { current.setDate(current.getDate() + 14); }
    else { current.setMonth(current.getMonth() + 1); }
  }
  return schedule;
}

export default function PaymentPlanModal({ patient, onClose, onSaved }) {
  const patientId = patient?.patient_id ?? patient?.id;

  const [step, setStep] = useState('setup'); // 'setup' | 'review'

  // Claims
  const [claims, setClaims]             = useState([]);
  const [claimsLoading, setClaimsLoading] = useState(true);

  // Selections
  const [selectedClaimIds, setSelectedClaimIds] = useState(new Set());
  const [allocations, setAllocations]           = useState({}); // claim_id → amount string

  // Schedule form
  const [form, setForm] = useState({
    installment_amount: '',
    frequency:          'monthly',
    start_date:         today(),
    notes:              '',
  });

  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [success, setSuccess] = useState(null); // plan_number

  useEffect(() => {
    if (!patientId) return;
    fetchPatientOutstandingClaims(patientId)
      .then((res) => {
        const all = res.data ?? [];
        setClaims(all);
        // Pre-select claims with outstanding > 0
        const presel = new Set(all.filter((c) => c.outstanding > 0).map((c) => c.claim_id));
        setSelectedClaimIds(presel);
        const prealloc = {};
        all.filter((c) => c.outstanding > 0).forEach((c) => {
          prealloc[c.claim_id] = c.outstanding.toFixed(2);
        });
        setAllocations(prealloc);
      })
      .catch(() => setClaims([]))
      .finally(() => setClaimsLoading(false));
  }, [patientId]);

  const toggleClaim = (claimId, outstanding) => {
    setSelectedClaimIds((prev) => {
      const next = new Set(prev);
      if (next.has(claimId)) {
        next.delete(claimId);
        setAllocations((a) => { const n = { ...a }; delete n[claimId]; return n; });
      } else {
        next.add(claimId);
        setAllocations((a) => ({ ...a, [claimId]: (parseFloat(outstanding) || 0).toFixed(2) }));
      }
      return next;
    });
  };

  const totalAllocated = useMemo(() => {
    return [...selectedClaimIds].reduce((sum, id) => sum + (parseFloat(allocations[id]) || 0), 0);
  }, [selectedClaimIds, allocations]);

  const schedule = useMemo(() => {
    return buildSchedule(totalAllocated, form.installment_amount, form.frequency, form.start_date);
  }, [totalAllocated, form.installment_amount, form.frequency, form.start_date]);

  const facilityId = useMemo(() => {
    if (!claims.length) return patient?.facility_id ?? null;
    // All claims share the same patient/facility
    return patient?.facility_id ?? null;
  }, [claims, patient]);

  const canProceed = selectedClaimIds.size > 0 && totalAllocated > 0 &&
    parseFloat(form.installment_amount) > 0 && form.start_date;

  const handleCreate = async () => {
    setError(null);
    setSaving(true);
    try {
      const claimsList = [...selectedClaimIds].map((id) => ({
        claim_id:         id,
        allocated_amount: parseFloat(allocations[id]) || 0,
      }));

      const res = await createPaymentPlan(patientId, {
        facility_id:       facilityId,
        total_amount:      totalAllocated,
        installment_amount: parseFloat(form.installment_amount),
        frequency:          form.frequency,
        start_date:         form.start_date,
        notes:              form.notes || null,
        claims:             claimsList,
      });
      setSuccess(res.data?.plan_number ?? 'PP-NEW');
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to create payment plan.');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="ppm-overlay">
      <div className="ppm-panel">

        {/* Header */}
        <div className="ppm-header">
          <div>
            <h2 className="ppm-title">Create Payment Plan</h2>
            <p className="ppm-sub">{patient?.first_name} {patient?.last_name}</p>
          </div>
          <button className="ppm-close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Step indicator */}
        <div className="ppm-steps">
          {['Setup', 'Review'].map((label, idx) => {
            const active = (idx === 0 && step === 'setup') || (idx === 1 && step === 'review');
            const done   = idx === 0 && step === 'review';
            return (
              <div key={label} className={`ppm-step ${active ? 'active' : ''} ${done ? 'done' : ''}`}>
                <div className="ppm-step-dot">
                  {done
                    ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    : idx + 1
                  }
                </div>
                <span className="ppm-step-label">{label}</span>
                {idx < 1 && <div className="ppm-step-line" />}
              </div>
            );
          })}
        </div>

        {/* Body */}
        <div className="ppm-body">

          {/* ── Success ── */}
          {success && (
            <div className="ppm-success">
              <div className="ppm-success-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><polyline points="9 12 11 14 15 10" />
                </svg>
              </div>
              <h3 className="ppm-success-title">Payment Plan Created</h3>
              <p className="ppm-success-msg">
                Plan <strong>{success}</strong> — {fmt(totalAllocated)} over {schedule.length} installment{schedule.length !== 1 ? 's' : ''}<br />
                Payoff by {fmtDate(schedule[schedule.length - 1]?.date)}
              </p>
            </div>
          )}

          {/* ── Step 1: Setup ── */}
          {!success && step === 'setup' && (
            <>
              {/* Claims section */}
              <div className="ppm-section-title">Select Claims & Allocate Amounts</div>
              {claimsLoading ? (
                <div className="ppm-loading"><div className="ppm-spinner" /><span>Loading claims…</span></div>
              ) : claims.length === 0 ? (
                <div className="ppm-empty">No claims found for this patient.</div>
              ) : (
                <div className="ppm-claims-table-wrap">
                  <table className="ppm-claims-table">
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}></th>
                        <th>Claim #</th>
                        <th>Svc Date</th>
                        <th className="r">Outstanding</th>
                        <th className="r">Allocate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {claims.map((c) => {
                        const checked = selectedClaimIds.has(c.claim_id);
                        return (
                          <tr key={c.claim_id} className={c.outstanding <= 0 ? 'ppm-row-dim' : ''}>
                            <td>
                              <input
                                type="checkbox"
                                className="ppm-checkbox"
                                checked={checked}
                                onChange={() => toggleClaim(c.claim_id, c.outstanding)}
                                disabled={c.outstanding <= 0}
                              />
                            </td>
                            <td className="ppm-claim-num">{c.claim_number}</td>
                            <td>{fmtDate(c.service_date)}</td>
                            <td className="r">
                              {c.outstanding > 0
                                ? <strong>{fmt(c.outstanding)}</strong>
                                : <span className="ppm-paid-tag">Paid</span>
                              }
                            </td>
                            <td className="r">
                              {checked ? (
                                <div className="ppm-alloc-wrap">
                                  <span className="ppm-alloc-sym">$</span>
                                  <input
                                    className="ppm-alloc-input"
                                    type="number"
                                    min="0.01"
                                    step="0.01"
                                    value={allocations[c.claim_id] ?? ''}
                                    onChange={(e) => setAllocations((a) => ({ ...a, [c.claim_id]: e.target.value }))}
                                  />
                                </div>
                              ) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Totals bar */}
              {selectedClaimIds.size > 0 && (
                <div className="ppm-total-bar">
                  <span>Plan Total:</span>
                  <strong>{fmt(totalAllocated)}</strong>
                </div>
              )}

              {/* Schedule form */}
              <div className="ppm-section-title" style={{ marginTop: 20 }}>Installment Schedule</div>
              <div className="ppm-form-grid">
                <div className="ppm-field">
                  <label className="ppm-label">Installment Amount</label>
                  <div className="ppm-money-wrap">
                    <span className="ppm-money-sym">$</span>
                    <input
                      className="ppm-input ppm-money-input"
                      type="number"
                      min="1"
                      step="0.01"
                      placeholder="0.00"
                      value={form.installment_amount}
                      onChange={(e) => setForm((f) => ({ ...f, installment_amount: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="ppm-field">
                  <label className="ppm-label">Frequency</label>
                  <select
                    className="ppm-input"
                    value={form.frequency}
                    onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value }))}
                  >
                    {FREQUENCIES.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
                <div className="ppm-field">
                  <label className="ppm-label">Start Date</label>
                  <input
                    className="ppm-input"
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                  />
                </div>
              </div>

              {/* Live preview */}
              {schedule.length > 0 && (
                <div className="ppm-preview">
                  <span className="ppm-preview-pill">
                    {schedule.length} installment{schedule.length !== 1 ? 's' : ''} of {fmt(schedule[0].amount)}
                    {schedule[schedule.length - 1]?.amount !== schedule[0]?.amount &&
                      `, last ${fmt(schedule[schedule.length - 1]?.amount)}`
                    }
                  </span>
                  <span className="ppm-preview-pill">
                    Payoff by {fmtDate(schedule[schedule.length - 1]?.date)}
                  </span>
                </div>
              )}

              <div className="ppm-field ppm-field-full" style={{ marginTop: 14 }}>
                <label className="ppm-label">Notes</label>
                <textarea
                  className="ppm-textarea"
                  rows="2"
                  placeholder="Optional"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </div>
            </>
          )}

          {/* ── Step 2: Review ── */}
          {!success && step === 'review' && (
            <div className="ppm-review">
              <div className="ppm-review-summary">
                <div className="ppm-rs-row"><span>Patient</span><strong>{patient?.first_name} {patient?.last_name}</strong></div>
                <div className="ppm-rs-row"><span>Plan Total</span><strong>{fmt(totalAllocated)}</strong></div>
                <div className="ppm-rs-row"><span>Installment</span><strong>{fmt(form.installment_amount)} / {FREQUENCIES.find((f) => f.value === form.frequency)?.label}</strong></div>
                <div className="ppm-rs-row"><span>First Due</span><strong>{fmtDate(schedule[0]?.date)}</strong></div>
                <div className="ppm-rs-row"><span>Payoff Date</span><strong>{fmtDate(schedule[schedule.length - 1]?.date)}</strong></div>
                <div className="ppm-rs-row"><span>Total Installments</span><strong>{schedule.length}</strong></div>
              </div>

              <div className="ppm-section-title" style={{ marginTop: 20 }}>Installment Schedule</div>
              <div className="ppm-schedule-wrap">
                <table className="ppm-schedule-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Due Date</th>
                      <th className="r">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.map((inst) => (
                      <tr key={inst.number}>
                        <td className="ppm-inst-num">{inst.number}</td>
                        <td>{fmtDate(inst.date)}</td>
                        <td className="r">{fmt(inst.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="ppm-section-title" style={{ marginTop: 20 }}>Claim Allocations</div>
              {[...selectedClaimIds].map((id) => {
                const c = claims.find((cl) => cl.claim_id === id);
                return c ? (
                  <div key={id} className="ppm-alloc-summary-row">
                    <span>{c.claim_number} — {fmtDate(c.service_date)}</span>
                    <strong>{fmt(allocations[id])}</strong>
                  </div>
                ) : null;
              })}

              {error && <div className="ppm-error">{error}</div>}
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="ppm-footer">
          {success ? (
            <button className="ppm-btn-primary" onClick={() => { onSaved?.(); onClose(); }}>Done</button>
          ) : step === 'setup' ? (
            <>
              <button className="ppm-btn-secondary" onClick={onClose}>Cancel</button>
              <button className="ppm-btn-primary" onClick={() => setStep('review')} disabled={!canProceed}>
                Review Plan →
              </button>
            </>
          ) : (
            <>
              <button className="ppm-btn-secondary" onClick={() => setStep('setup')} disabled={saving}>← Back</button>
              <button className="ppm-btn-primary" onClick={handleCreate} disabled={saving}>
                {saving ? 'Creating…' : 'Create Plan'}
              </button>
            </>
          )}
        </div>

      </div>
    </div>
  );
}
